'use client';

import NextLink from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { trackCTAClick } from '../../lib/analytics';
import { getSampleBotUrl, type UseCaseKey } from '../../lib/sampleBots';

/**
 * Homepage use-cases section (Story 19.50 + 19.51).
 *
 * Surfaces concrete use cases out of the footer and into the page body so cold
 * visitors understand what Boty is for. Each card links to its dedicated
 * use-case page ("Learn more") and to a LIVE sample bot ("See a live example")
 * the visitor can actually chat with.
 */
const USE_CASES: { key: UseCaseKey; emoji: string; page: string }[] = [
  { key: 'events', emoji: '🎉', page: 'event-faq-bot' },
  { key: 'services', emoji: '🔧', page: 'service-bot' },
  { key: 'personalBrands', emoji: '🎤', page: 'personal-brand-bot' },
];

export function UseCasesSection() {
  const t = useTranslations('creator.landing.useCasesSection');
  const locale = useLocale();
  const isRtl = useTheme().direction === 'rtl';

  return (
    <Box component="section" sx={{ mt: { xs: 5, md: 8 }, mb: { xs: 2, md: 4 } }}>
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
        <Typography variant="h4" component="h2" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
          {t('title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
          {t('subtitle')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          alignItems: 'stretch',
        }}
      >
        {USE_CASES.map(({ key, emoji, page }) => (
          <Box
            key={key}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: { xs: 2.5, md: 3 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              transition: 'box-shadow 0.2s, border-color 0.2s',
              '&:hover': { boxShadow: 3, borderColor: 'primary.main' },
            }}
          >
            <Typography component="span" sx={{ fontSize: '2rem', lineHeight: 1 }} aria-hidden>
              {emoji}
            </Typography>
            <Typography variant="h6" component="h3" fontWeight={600}>
              {t(`${key}.title`)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
              {t(`${key}.description`)}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
              {/* Primary: try a real, live bot (Story 19.51) */}
              <Button
                component={MuiLink}
                href={getSampleBotUrl(key, locale)}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="small"
                endIcon={<OpenInNewIcon />}
                onClick={() => trackCTAClick('use_case_live_example', 'homepage_use_cases', key)}
                sx={{ textTransform: 'none' }}
              >
                {t('liveExample')}
              </Button>
              {/* Secondary: learn more on the dedicated use-case page */}
              <Button
                component={NextLink}
                href={`/${locale}/${page}`}
                variant="text"
                size="small"
                endIcon={<ArrowForwardIcon sx={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
                onClick={() => trackCTAClick('use_case_learn_more', 'homepage_use_cases', key)}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                {t('learnMore')}
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
